import clsx from "clsx";
import { CSVFrozenHeaderMapping } from "csv/mappings";
import React from "react";
import dropdownEditorFactory from "../../../components/DataGrid/DropdownEditor";
import { ColumnExtraOptions } from "../../../components/DataGrid/types";
import { mappingRMWorking } from "../../../csv/mappings/collect";
import { IRow } from "../../../csv/types";
import { useStore } from "../../../store";
import {
    getActiveColumn,
    getGivenCell,
    getGivenNumberCell,
    getInputCell,
    getNumberCell,
    idCell,
} from "./utils";

const schemaRMWorking = (
  key: string,
  sheetEditable?: boolean,
  frozenIdentifiers?: CSVFrozenHeaderMapping
) => {
  const defaultCell = {
    name: mappingRMWorking[key] ? mappingRMWorking[key][0] : key,
    width: mappingRMWorking[key] ? mappingRMWorking[key][1] : 50,
  };
  const givenCellRM = {
    ...getGivenCell(true),
    ...defaultCell,
  };
  const unfrozenGivenCellRM = {
    ...defaultCell,
    ...getGivenCell(false),
  };
  const inputCell = {
    ...getInputCell(sheetEditable ?? true),
    ...defaultCell,
  };
  const numberCell = {
    ...getNumberCell(sheetEditable ?? true),
    ...defaultCell,
  };
  const numberCell0 = {
    ...getNumberCell(sheetEditable ?? true, 0, 0),
    ...defaultCell,
  };
  const numberCellCurrency = {
    ...getNumberCell(sheetEditable ?? true, 2, 2),
    ...defaultCell,
  };
  const givenNumberCellCurrencyRM = {
    ...getGivenNumberCell(2, 2),
    ...defaultCell,
  };

  const options: Record<string, ColumnExtraOptions<IRow>> = {
    id: idCell,
    concatenate: givenCellRM,
    active: getActiveColumn(frozenIdentifiers?.active),
    r_code: !frozenIdentifiers?.r_code ? unfrozenGivenCellRM : givenCellRM,
    supplier_product_name: !frozenIdentifiers?.supplier_product_name
      ? unfrozenGivenCellRM
      : givenCellRM,
    plant: !frozenIdentifiers?.plant ? unfrozenGivenCellRM : givenCellRM,
    source_clt: !frozenIdentifiers?.source_clt
      ? unfrozenGivenCellRM
      : givenCellRM,
    sap_no: !frozenIdentifiers?.sap_no ? unfrozenGivenCellRM : givenCellRM,
    supplier: inputCell,
    sap_stock_location: inputCell,
    inco_term: inputCell,
    bulk_or_drum: {
      ...inputCell,
      editor: (props) => {
        const Dropdown = dropdownEditorFactory(key, [
          { value: "Bulk", label: "Bulk" },
          { value: "Drum", label: "Drum" },
        ]);
        return <Dropdown {...props} />;
      },
      editorOptions: {
        editOnClick: true,
      },
    },
    rm_purchase_price: {
      ...numberCellCurrency,
      colSpan(args: any) {
        if (args.row && !args.row.id) {
          return 5;
        }
      },
      // Only read-only ("given") when this row's price is sourced from RMPA (Define).
      // For any other price_obtained_from value, RM Price remains editable as usual.
      editable: (row: IRow) => {
        if (!row.id || !sheetEditable) {
          return false;
        }
        return row["price_obtained_from"] !== "RMPA";
      },
      cellClass(row) {
        if (!row.id) {
          return "cell-subheader";
        }
        if (row["price_obtained_from"] === "RMPA") {
          return (givenNumberCellCurrencyRM.cellClass! as Function)(row);
        }
        return (numberCellCurrency.cellClass! as Function)(row);
      },
      formatter: (props) => {
        if (!props.row.id) {
          return <>{props.row.rm_purchase_price}</>;
        }
        const Formatter = numberCellCurrency.formatter as React.ComponentType;
        return <Formatter {...props} />;
      },
    },
    supply_country: inputCell,
    supplier_freight: {
      ...numberCellCurrency,
      // Only read-only ("given") when this row's price is sourced from RMPA (Define).
      editable: (row: IRow) => {
        if (!row.id || !sheetEditable) {
          return false;
        }
        return row["price_obtained_from"] !== "RMPA";
      },
      cellClass(row) {
        if (!row.id) {
          return undefined;
        }
        if (row["price_obtained_from"] === "RMPA") {
          return (givenNumberCellCurrencyRM.cellClass! as Function)(row);
        }
        return (numberCellCurrency.cellClass! as Function)(row);
      },
    },
    drumming_differential_per_mt: {
      ...getNumberCell(sheetEditable ?? true, 2, 2),
      ...defaultCell,
      editable: (row: IRow) => {
        if (row["price_obtained_from"] === "RMPA") {
          return false;
        }
        return row["bulk_or_drum"] !== "Bulk";
      },
      cellClass: (row: IRow) => {
        if (!sheetEditable) {
          return "cell-readonly";
        }
        if (row["price_obtained_from"] === "RMPA") {
          return (givenNumberCellCurrencyRM.cellClass! as Function)(row);
        }
        return row["bulk_or_drum"] !== "Bulk"
          ? clsx((inputCell.cellClass! as Function)(row), "cell-numeric")
          : "cell-input cell-disallowed";
      },
    },
    rebates_per_mt: {
      ...numberCellCurrency,
      // Only read-only ("given") when this row's price is sourced from RMPA (Define).
      editable: (row: IRow) => {
        if (!row.id || !sheetEditable) {
          return false;
        }
        return row["price_obtained_from"] !== "RMPA";
      },
      cellClass(row) {
        if (!row.id) {
          return undefined;
        }
        if (row["price_obtained_from"] === "RMPA") {
          return (givenNumberCellCurrencyRM.cellClass! as Function)(row);
        }
        return (numberCellCurrency.cellClass! as Function)(row);
      },
    },
    discounts_per_mt: {
      ...numberCellCurrency,
      // Only read-only ("given") when this row's price is sourced from RMPA (Define).
      editable: (row: IRow) => {
        if (!row.id || !sheetEditable) {
          return false;
        }
        return row["price_obtained_from"] !== "RMPA";
      },
      cellClass(row) {
        if (!row.id) {
          return undefined;
        }
        if (row["price_obtained_from"] === "RMPA") {
          return (givenNumberCellCurrencyRM.cellClass! as Function)(row);
        }
        return (numberCellCurrency.cellClass! as Function)(row);
      },
    },
    supply_storage_handling_fee: {
      ...numberCellCurrency,
      // Only read-only ("given") when this row's price is sourced from RMPA (Define).
      editable: (row: IRow) => {
        if (!row.id || !sheetEditable) {
          return false;
        }
        return row["price_obtained_from"] !== "RMPA";
      },
      cellClass(row) {
        if (!row.id) {
          return undefined;
        }
        if (row["price_obtained_from"] === "RMPA") {
          return (givenNumberCellCurrencyRM.cellClass! as Function)(row);
        }
        return (numberCellCurrency.cellClass! as Function)(row);
      },
    },
    tax_duties: {
      ...numberCellCurrency,
      // Only read-only ("given") when this row's price is sourced from RMPA (Define).
      editable: (row: IRow) => {
        if (!row.id || !sheetEditable) {
          return false;
        }
        return row["price_obtained_from"] !== "RMPA";
      },
      cellClass(row) {
        if (!row.id) {
          return undefined;
        }
        if (row["price_obtained_from"] === "RMPA") {
          return (givenNumberCellCurrencyRM.cellClass! as Function)(row);
        }
        return (numberCellCurrency.cellClass! as Function)(row);
      },
    },
    other_costs: {
      ...numberCellCurrency,
      // Only read-only ("given") when this row's price is sourced from RMPA (Define).
      editable: (row: IRow) => {
        if (!row.id || !sheetEditable) {
          return false;
        }
        return row["price_obtained_from"] !== "RMPA";
      },
      cellClass(row) {
        if (!row.id) {
          return undefined;
        }
        if (row["price_obtained_from"] === "RMPA") {
          return (givenNumberCellCurrencyRM.cellClass! as Function)(row);
        }
        return (numberCellCurrency.cellClass! as Function)(row);
      },
    },
    total: {
      ...numberCell0,
      // Only read-only ("given") when this row's price is sourced from RMPA (Define);
      // otherwise Total is a regular editable field like the other cost components.
      editable: (row: IRow) => {
        if (!row.id || !sheetEditable) {
          return false;
        }
        return row["price_obtained_from"] !== "RMPA";
      },
      cellClass(row) {
        if (!row.id) {
          return "cell-subheader";
        }
        if (row["price_obtained_from"] === "RMPA") {
          return clsx((numberCell0.cellClass! as Function)(row), "cell-given");
        }
        return (numberCell0.cellClass! as Function)(row);
      },
      formatter: (props) => {
        if (!props.row.id) {
          return <>{props.row.total}</>;
        }
        if (props.row["price_obtained_from"] !== "RMPA") {
          const Formatter = numberCell0.formatter as React.ComponentType;
          return <Formatter {...props} />;
        }
        const toNumber = (value: unknown) => {
          const parsed = Number(value);
          return Number.isFinite(parsed) ? parsed : 0;
        };
        const total =
          toNumber(props.row.rm_purchase_price) +
          toNumber(props.row.supplier_freight) +
          toNumber(props.row.drumming_differential_per_mt) +
          toNumber(props.row.rebates_per_mt) +
          toNumber(props.row.discounts_per_mt) +
          toNumber(props.row.supply_storage_handling_fee) +
          toNumber(props.row.tax_duties) +
          toNumber(props.row.other_costs);
        return (
          <>{new Intl.NumberFormat("en-GB", { maximumFractionDigits: 4 }).format(total)}</>
        );
      },
    },
    freight_cost: {
      ...numberCellCurrency,
      cellClass(row) {
        if (!row.id) {
          return "cell-subheader";
        }
        return (numberCellCurrency.cellClass! as Function)(row);
      },
      formatter: (props) => {
        if (!props.row.id) {
          return <>{props.row.freight_cost}</>;
        }
        const Formatter = numberCellCurrency.formatter as React.ComponentType;
        return <Formatter {...props} />;
      },
    },
    currency_of_purchase: {
      ...inputCell,
      editor: function Editor(props) {
        const store = useStore();
        const Dropdown = dropdownEditorFactory(
          key,
          store.assets.currencySelect.map(({ code }) => ({
            value: code,
            label: code,
          }))
        );
        return <Dropdown {...props} />;
      },
      editorOptions: {
        editOnClick: true,
      },
    },
    unit_of_measure: {
      ...inputCell,
      editor: function Editor(props) {
        const store = useStore();
        const Dropdown = dropdownEditorFactory(
          key,
          store.assets.unitSelect.map(({ label }) => ({
            value: label,
            label,
          }))
        );
        return <Dropdown {...props} />;
      },
      editorOptions: {
        editOnClick: true,
      },
    },
    price_obtained_from: {
      ...inputCell,
      editor: (props) => {
        const Dropdown = dropdownEditorFactory(key, [
          { value: "PLMS", label: "PLMS" },
          { value: "RMPA", label: "RMPA" },
          { value: "Invoice", label: "Invoice" },
          { value: "PCTP list", label: "PCTP list" },
          { value: "Other", label: "Other" },
        ]);
        return <Dropdown {...props} />;
      },
      editorOptions: {
        editOnClick: true,
      },
    },
    cost_per_mt: {
      ...numberCell0,
      editable: false,
      colSpan(args: any) {
        if (args.row && !args.row.id) {
          return 2;
        }
      },
      cellClass(row) {
        if (!row.id) {
          return "cell-subheader";
        }
        return clsx((numberCell0.cellClass! as Function)(row), "cell-given");
      },
      formatter: (props) => {
        if (!props.row.id) {
          return <>{props.row.cost_per_mt}</>;
        }
        const Formatter = numberCell0.formatter as React.ComponentType;
        return <Formatter {...props} />;
      },
    },
    cost_per_mt_spec_curr: {
      ...numberCell0,
      editable: false,
      cellClass(row) {
        if (!row.id) {
          return "cell-subheader";
        }
        return clsx((numberCell0.cellClass! as Function)(row), "cell-given");
      },
    },
  };
  return options[key] || inputCell;
};

export default schemaRMWorking;
